import React from 'react';
import { Link } from 'react-router-dom';
import './FeedStatusCard.css';

interface FeedBin {
  id: number;
  name: string;
  category: string;
  current_lbs: number;
  days_remaining: number | null;
  status: string;
  percent_full: number;
}

interface FeedStatusCardProps {
  bins: FeedBin[];
}

const CATEGORY_EMOJI: Record<string, string> = {
  poultry: '🐔',
  dog: '🐕',
  cat: '🐱',
  goat: '🐐',
  sheep: '🐑',
  cattle: '🐄',
  horse: '🐴',
  swine: '🐷',
  rabbit: '🐰',
  other: '📦',
};

const getStatusColor = (days: number | null): string => {
  if (days === null) return 'unknown';
  if (days >= 30) return 'good';
  if (days >= 14) return 'ok';
  if (days >= 7) return 'low';
  if (days >= 3) return 'low';
  return 'critical';
};

const getStatusFromDays = (days: number | null): 'critical' | 'low' | null => {
  if (days === null) return null;
  if (days <= 3) return 'critical';
  if (days <= 7) return 'low';
  return null;
};

const FeedStatusCard: React.FC<FeedStatusCardProps> = ({ bins }) => {
  if (bins.length === 0) {
    return (
      <div className="feed-status-card feed-status-empty">
        <div className="feed-status-header">
          <span className="feed-status-icon">📊</span>
          <span className="feed-status-title">Feed Status</span>
        </div>
        <div className="feed-status-empty-content">
          <p>No feed bins set up yet</p>
          <Link to="/bridge-club/feed" className="feed-status-setup-link">
            Set up feed tracking →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-status-card">
      <div className="feed-status-header">
        <div className="feed-status-header-left">
          <span className="feed-status-icon">📊</span>
          <span className="feed-status-title">Feed Status</span>
        </div>
        <Link to="/bridge-club/feed" className="feed-status-link">
          Manage →
        </Link>
      </div>

      <div className="feed-status-list">
        {bins.map(bin => {
          const statusColor = getStatusColor(bin.days_remaining);
          const alertStatus = getStatusFromDays(bin.days_remaining);
          const emoji = CATEGORY_EMOJI[bin.category] || '📦';

          return (
            <div
              key={bin.id}
              className={`feed-status-item feed-status-${statusColor}${alertStatus ? ` feed-alert-${alertStatus}` : ''}`}
            >
              <span className="feed-bin-emoji">{emoji}</span>
              <div className="feed-bin-info">
                <span className="feed-bin-name">{bin.name}</span>
                <div className="feed-bin-bar-container">
                  <div className="feed-bin-bar">
                    <div
                      className="feed-bin-bar-fill"
                      style={{ width: `${Math.min(100, bin.percent_full || 0)}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="feed-bin-status">
                <span className="feed-bin-days">
                  {bin.days_remaining !== null ? `${bin.days_remaining}d` : '?'}
                </span>
                {alertStatus === 'critical' && (
                  <span className="feed-badge feed-badge-critical">Critical</span>
                )}
                {alertStatus === 'low' && (
                  <span className="feed-badge feed-badge-low">Low</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeedStatusCard;
